import path from "path";
import net from "node:net";
import dns from "node:dns/promises";

import mime from "mime";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { HttpsProxyAgent } from "https-proxy-agent";
import { SocksProxyAgent } from "socks-proxy-agent";

import APIException from "@/core/errors/api-exception.ts";
import EX from "@/api/constants/error-codes.ts";
import logger from "@/core/utils/logger.ts";
import util from "@/core/utils/util.ts";
import { JimengErrorHandler, JimengErrorResponse } from "@/core/errors/error-handler.ts";
import { BASE_URL_DREAMINA_US, BASE_URL_DREAMINA_HK, DA_VERSION, WEB_VERSION } from "@/api/constants/dreamina.ts";

import {
  BASE_URL_CN,
  BASE_URL_US_COMMERCE,
  BASE_URL_HK,
  DEFAULT_ASSISTANT_ID_CN,
  DEFAULT_ASSISTANT_ID_US,
  DEFAULT_ASSISTANT_ID_HK,
  DEFAULT_ASSISTANT_ID_JP,
  DEFAULT_ASSISTANT_ID_SG,
  PLATFORM_CODE,
  REGION_CN,
  REGION_US,
  REGION_HK,
  REGION_JP,
  REGION_SG,
  VERSION_CODE,
  RETRY_CONFIG
} from "@/api/constants/common.ts";

export type RegionCode = "cn" | "us" | "hk" | "jp" | "sg";

// 模型名称
const MODEL_NAME = "jimeng";
// 设备ID
const DEVICE_ID = Math.random() * 999999999999999999 + 7000000000000000000;
// WebID
const WEB_ID = Math.random() * 999999999999999999 + 7000000000000000000;
// 用户ID（32位hex，无横线）
const USER_ID = util.uuid(false);
// 国际区前端域名（Origin/Referer 用于跨域 commerce 请求）
export const INTERNATIONAL_FRONTEND_ORIGIN = "https://dreamina.capcut.com";
// 伪装headers
const FAKE_HEADERS = {
  Accept: "application/json, text/plain, */*",
  "Accept-Encoding": "gzip, deflate, br, zstd",
  "Accept-language": "zh-CN,zh;q=0.9",
  "Cache-control": "no-cache",
  Appvr: VERSION_CODE,
  Pragma: "no-cache",
  Priority: "u=1, i",
  Pf: PLATFORM_CODE,
  "Sec-Ch-Ua": '"Google Chrome";v="142", "Chromium";v="142", "Not_A Brand";v="99"',
  "Sec-Ch-Ua-Mobile": "?0",
  "Sec-Ch-Ua-Platform": '"Windows"',
  "Sec-Fetch-Dest": "empty",
  "Sec-Fetch-Mode": "cors",
  "Sec-Fetch-Site": "same-origin",
  "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36",
};
// 文件最大大小
const FILE_MAX_SIZE = 100 * 1024 * 1024;

function isPrivateOrLocalIp(address: string): boolean {
  const normalized = address.toLowerCase().split("%")[0];
  const ipVersion = net.isIP(normalized);
  if (ipVersion === 4) {
    const octets = normalized.split(".").map((part) => Number(part));
    const [a, b] = octets;
    if (a === 10) return true;
    if (a === 127) return true;
    if (a === 0) return true;
    if (a === 169 && b === 254) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 192 && b === 168) return true;
    return false;
  }
  if (ipVersion === 6) {
    if (normalized === "::1" || normalized === "::") return true;
    if (normalized.startsWith("fc") || normalized.startsWith("fd")) return true;
    if (normalized.startsWith("fe8") || normalized.startsWith("fe9") || normalized.startsWith("fea") || normalized.startsWith("feb")) return true;
    if (normalized.startsWith("::ffff:127.")) return true;
    return false;
  }
  return false;
}

export async function assertSafeExternalHttpUrl(fileUrl: string): Promise<void> {
  let parsed: URL;
  try {
    parsed = new URL(fileUrl);
  } catch {
    throw new APIException(EX.API_FILE_URL_INVALID, `File URL is invalid: ${fileUrl}`);
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new APIException(EX.API_FILE_URL_INVALID, `File URL protocol is not supported: ${parsed.protocol}`);
  }

  const hostname = parsed.hostname.trim().toLowerCase();
  if (!hostname) {
    throw new APIException(EX.API_FILE_URL_INVALID, "File URL hostname is empty");
  }
  if (hostname === "localhost" || hostname.endsWith(".local")) {
    throw new APIException(EX.API_FILE_URL_INVALID, `File URL host is not allowed: ${hostname}`);
  }

  if (net.isIP(hostname)) {
    if (isPrivateOrLocalIp(hostname)) {
      throw new APIException(EX.API_FILE_URL_INVALID, `File URL host is not allowed: ${hostname}`);
    }
    return;
  }

  let records: Array<{ address: string; family: number }>;
  try {
    records = await dns.lookup(hostname, { all: true, verbatim: true });
  } catch {
    throw new APIException(EX.API_FILE_URL_INVALID, `File URL hostname cannot be resolved: ${hostname}`);
  }

  if (!records.length) {
    throw new APIException(EX.API_FILE_URL_INVALID, `File URL hostname cannot be resolved: ${hostname}`);
  }

  for (const record of records) {
    if (isPrivateOrLocalIp(record.address)) {
      throw new APIException(EX.API_FILE_URL_INVALID, `File URL host is not allowed: ${hostname}`);
    }
  }
}

/**
 * 获取缓存中的access_token
 *
 * 目前jimeng的access_token是固定的，暂无刷新功能
 *
 * @param refreshToken 用于刷新access_token的refresh_token
 */
export async function acquireToken(refreshToken: string): Promise<string> {
  return refreshToken;
}

/**
 * 解析 token 中的地区信息
 *
 * @param refreshToken 刷新令牌
 * @returns 地区信息对象
 */
export interface RegionInfo {
  isUS: boolean;
  isHK: boolean;
  isJP: boolean;
  isSG: boolean;
  isInternational: boolean;
  isCN: boolean;
}

const REGION_PREFIX_PATTERN = /^(us|hk|jp|sg)-/i;

export function parseRegionCode(value: unknown): RegionCode | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "cn" || normalized === "us" || normalized === "hk" || normalized === "jp" || normalized === "sg") {
    return normalized as RegionCode;
  }
  return null;
}

export function buildRegionInfo(regionCode: RegionCode): RegionInfo {
  return {
    isUS: regionCode === "us",
    isHK: regionCode === "hk",
    isJP: regionCode === "jp",
    isSG: regionCode === "sg",
    isInternational: regionCode !== "cn",
    isCN: regionCode === "cn",
  };
}

export function parseRegionFromHeader(headerValue: unknown): RegionInfo | null {
  const regionCode = parseRegionCode(headerValue);
  if (!regionCode) return null;
  return buildRegionInfo(regionCode);
}

export function regionInfoToRegionCode(regionInfo: RegionInfo): RegionCode {
  if (regionInfo.isUS) return "us";
  if (regionInfo.isHK) return "hk";
  if (regionInfo.isJP) return "jp";
  if (regionInfo.isSG) return "sg";
  return "cn";
}

export function assertTokenWithoutRegionPrefix(rawToken: string): void {
  const { token } = parseProxyFromToken(rawToken);
  if (REGION_PREFIX_PATTERN.test(token.trim())) {
    throw new APIException(
      EX.API_REQUEST_FAILED,
      "token 前缀协议已移除，请使用纯 token，并通过 token-pool 的 region 字段或请求头 X-Region 指定区域"
    );
  }
}

export interface TokenWithProxy {
  token: string;
  proxyUrl: string | null;
}

export function parseProxyFromToken(rawToken: string): TokenWithProxy {
  const tokenValue = rawToken.trim();
  const proxyPattern = /^(https?|socks(?:4|5)?):\/\//i;
  if (!proxyPattern.test(tokenValue)) return { token: tokenValue, proxyUrl: null };

  const lastAtIndex = tokenValue.lastIndexOf("@");
  if (lastAtIndex <= 0 || lastAtIndex === tokenValue.length - 1)
    return { token: tokenValue, proxyUrl: null };

  const proxyUrl = tokenValue.slice(0, lastAtIndex);
  const token = tokenValue.slice(lastAtIndex + 1);
  if (!proxyUrl || !token) return { token: tokenValue, proxyUrl: null };

  return { token, proxyUrl };
}

export function parseRegionFromToken(refreshToken: string): RegionInfo {
  throw new APIException(
    EX.API_REQUEST_FAILED,
    "parseRegionFromToken 已废弃。token 前缀协议已移除，请改为显式传入 region 上下文"
  );
}

/**
 * 根据地区获取 Referer
 *
 * @param refreshToken 刷新令牌
 * @param cnPath 国内站路径
 * @returns Referer URL
 */
export function getRefererByRegion(regionInfo: RegionInfo, cnPath: string, intlPath?: string): string {
  const base = regionInfo.isInternational ? INTERNATIONAL_FRONTEND_ORIGIN : BASE_URL_CN;
  const path = regionInfo.isInternational ? (intlPath ?? "/") : cnPath;
  return `${base}${path}`;
}

/**
 * 根据地区获取 AssistantID
 *
 * @param regionInfo 地区信息
 * @returns AssistantID
 */
export function getAssistantId(regionInfo: RegionInfo): number {
  if (regionInfo.isUS) return DEFAULT_ASSISTANT_ID_US;
  if (regionInfo.isJP) return DEFAULT_ASSISTANT_ID_JP;
  if (regionInfo.isSG) return DEFAULT_ASSISTANT_ID_SG;
  if (regionInfo.isHK) return DEFAULT_ASSISTANT_ID_HK;
  return DEFAULT_ASSISTANT_ID_CN;
}

/**
 * 生成cookie
 */
export function generateCookie(refreshToken: string) {
  const { token: tokenWithRegion } = parseProxyFromToken(refreshToken);
  assertTokenWithoutRegionPrefix(tokenWithRegion);
  const token = tokenWithRegion;
  const sidGuardTtl = 5184000;
  const sidGuardIssuedAt = util.unixTimestamp();
  const sidGuardExpireAt = encodeURIComponent(
    new Date((sidGuardIssuedAt + sidGuardTtl) * 1000).toUTCString()
  ).replace(/%20/g, "+");

  return [
    `_tea_web_id=${WEB_ID}`,
    `is_staff_user=false`,
    `sid_guard=${token}%7C${sidGuardIssuedAt}%7C${sidGuardTtl}%7C${sidGuardExpireAt}`,
    `uid_tt=${USER_ID}`,
    `uid_tt_ss=${USER_ID}`,
    `sid_tt=${token}`,
    `sessionid=${token}`,
    `sessionid_ss=${token}`,
  ].join("; ");
}

/**
 * 获取积分信息
 *
 * @param refreshToken 用于刷新access_token的refresh_token
 */
export async function getCredit(refreshToken: string, regionInfo: RegionInfo) {
  const referer = getRefererByRegion(regionInfo, "/ai-tool/image/generate");
  const origin = regionInfo.isInternational ? INTERNATIONAL_FRONTEND_ORIGIN : undefined;

  const {
    credit: { gift_credit, purchase_credit, vip_credit }
  } = await request("POST", "/commerce/v1/benefits/user_credit", refreshToken, regionInfo, {
    data: {},
    headers: {
      Referer: referer,
      ...(origin ? { Origin: origin } : {}),
    },
    noDefaultParams: true
  });
  logger.info(`\n积分信息: \n赠送积分: ${gift_credit}, 购买积分: ${purchase_credit}, VIP积分: ${vip_credit}`);
  return {
    giftCredit: gift_credit,
    purchaseCredit: purchase_credit,
    vipCredit: vip_credit,
    totalCredit: gift_credit + purchase_credit + vip_credit
  }
}

/**
 * 接收今日积分（仅在积分为 0 时调用）
 *
 * @param refreshToken 用于刷新access_token的refresh_token
 */
export async function receiveCredit(refreshToken: string, regionInfo: RegionInfo) {
  logger.info("正在尝试收取今日积分...")
  const referer = getRefererByRegion(regionInfo, "/ai-tool/home");
  const origin = regionInfo.isInternational ? INTERNATIONAL_FRONTEND_ORIGIN : undefined;
  const timeZone = regionInfo.isUS
    ? "America/New_York"
    : regionInfo.isHK
      ? "Asia/Hong_Kong"
      : regionInfo.isJP
        ? "Asia/Tokyo"
        : regionInfo.isSG
          ? "Asia/Singapore"
          : "Asia/Shanghai";

  const { receive_quota } = await request("POST", "/commerce/v1/benefits/credit_receive", refreshToken, regionInfo, {
    data: {
      time_zone: timeZone
    },
    headers: {
      Referer: referer,
      ...(origin ? { Origin: origin } : {}),
    }
  });
  logger.info(`今日${receive_quota}积分收取成功`);
  return receive_quota;
}

/**
 * 请求jimeng
 *
 * @param method 请求方法
 * @param uri 请求路径
 * @param params 请求参数
 * @param headers 请求头
 */
export async function request(
  method: string,
  uri: string,
  refreshToken: string,
  regionInfo: RegionInfo,
  options: AxiosRequestConfig & { noDefaultParams?: boolean } = {}
) {
  const { token: tokenWithRegion, proxyUrl } = parseProxyFromToken(refreshToken);
  assertTokenWithoutRegionPrefix(tokenWithRegion);
  const { isUS, isHK, isJP, isSG } = regionInfo;
  await acquireToken(tokenWithRegion);
  const deviceTime = util.unixTimestamp();
  const sign = util.md5(
    `9e2c|${uri.slice(-7)}|${PLATFORM_CODE}|${VERSION_CODE}|${deviceTime}||11ac`
  );

  let baseUrl: string;
  let aid: number;
  let region: string;

  if (isUS) {
    if (uri.startsWith("/commerce/")) {
      baseUrl = BASE_URL_US_COMMERCE;
    } else {
      baseUrl = BASE_URL_DREAMINA_US;
    }
    aid = DEFAULT_ASSISTANT_ID_US;
    region = REGION_US;
  } else if (isHK || isJP || isSG) {
    // HK, JP and SG regions use the same SG base URL for non-commerce endpoints
    if (uri.startsWith("/commerce/")) {
      // Commerce endpoints route through the US commerce domain
      baseUrl = BASE_URL_US_COMMERCE;
    } else {
      baseUrl = BASE_URL_DREAMINA_HK;
    }
    if (isJP) {
      aid = DEFAULT_ASSISTANT_ID_JP;
      region = REGION_JP;
    } else if (isSG) {
      aid = DEFAULT_ASSISTANT_ID_SG;
      region = REGION_SG;
    } else {
      aid = DEFAULT_ASSISTANT_ID_HK;
      region = REGION_HK;
    }
  } else {
    // CN region
    baseUrl = BASE_URL_CN;
    aid = DEFAULT_ASSISTANT_ID_CN;
    region = REGION_CN;
  }

  const origin = new URL(baseUrl).origin;

  const fullUrl = `${baseUrl}${uri}`;
  const requestParams = options.noDefaultParams ? (options.params || {}) : {
    aid: aid,
    device_platform: "web",
    region: region,
    ...(isUS || isHK || isJP || isSG ? {} : { webId: WEB_ID }),
    da_version: DA_VERSION,
    os: "windows",
    web_component_open_flag: 1,
    web_version: WEB_VERSION,
    aigc_features: "app_lip_sync",
    ...(options.params || {}),
  };

  const headers = {
    ...FAKE_HEADERS,
    Origin: origin,
    Referer: origin,
    "App-Sdk-Version": "48.0.0",
    Appid: aid,
    Cookie: generateCookie(tokenWithRegion),
    "Device-Time": deviceTime,
    Lan: isUS ? "en" : isJP ? "ja" : (isHK || isSG) ? "en" : "zh-Hans",
    Loc: isUS ? "us" : isJP ? "jp" : isHK ? "hk" : isSG ? "sg" : "cn",
    Sign: sign,
    "Sign-Ver": "1",
    Tdid: "",
    ...(options.headers || {}),
  };

  logger.info(`发送请求: ${method.toUpperCase()} ${fullUrl}`);
  if (proxyUrl) {
    const maskedProxyUrl = proxyUrl.replace(/\/\/([^@/]+)@/i, "//***@");
    logger.info(`使用代理: ${maskedProxyUrl}`);
  }
  logger.info(`请求参数: ${JSON.stringify(requestParams)}`);
  logger.info(`请求数据: ${JSON.stringify(options.data || {})}`);

  const proxyAgent = proxyUrl
    ? (proxyUrl.toLowerCase().startsWith("socks")
      ? new SocksProxyAgent(proxyUrl)
      : new HttpsProxyAgent(proxyUrl))
    : undefined;

  // 添加重试逻辑
  let retries = 0;
  const maxRetries = RETRY_CONFIG.MAX_RETRY_COUNT;
  let lastError = null;

  while (retries <= maxRetries) {
    try {
      if (retries > 0) {
        logger.info(`第 ${retries} 次重试请求: ${method.toUpperCase()} ${fullUrl}`);
        // 重试前等待一段时间
        await new Promise(resolve => setTimeout(resolve, RETRY_CONFIG.RETRY_DELAY));
      }

      const { params: _p, headers: _h, noDefaultParams: _n, ...restOptions } = options;
      const response = await axios.request({
        method,
        url: fullUrl,
        params: requestParams,
        headers: headers,
        timeout: 45000, // 增加超时时间到45秒
        validateStatus: () => true, // 允许任何状态码
        ...restOptions,
        ...(proxyAgent ? { httpAgent: proxyAgent, httpsAgent: proxyAgent, proxy: false } : {}),
      });

      // 记录响应状态和头信息
      logger.info(`响应状态: ${response.status} ${response.statusText}`);

      // 流式响应直接返回response
      if (options.responseType == "stream") return response;

      // 记录响应数据摘要
      const responseJson = JSON.stringify(response.data);
      const responseDataSummary = responseJson.substring(0, 500) + (responseJson.length > 500 ? "..." : "");
      logger.info(`响应数据摘要: ${responseDataSummary}`);

      // 检查HTTP状态码
      if (response.status >= 400) {
        logger.warn(`HTTP错误: ${response.status} ${response.statusText}`);
        if (retries < maxRetries) {
          retries++;
          continue;
        }
      }

      return checkResult(response);
    }
    catch (error) {
      lastError = error;
      logger.error(`请求失败 (尝试 ${retries + 1}/${maxRetries + 1}): ${error.message}`);

      // 如果是网络错误或超时，尝试重试
      // 包含常见的网络错误：ECONNRESET（连接重置）、ENOTFOUND（DNS解析失败）、
      // ECONNREFUSED（连接被拒绝）、EAI_AGAIN（DNS临时失败）、EPIPE（管道破裂）
      const retryableErrorCodes = [
        'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET', 'ENOTFOUND',
        'ECONNREFUSED', 'EAI_AGAIN', 'EPIPE', 'ENETUNREACH', 'EHOSTUNREACH'
      ];
      const isRetryableError = retryableErrorCodes.includes(error.code) ||
        error.message.includes('timeout') ||
        error.message.includes('network') ||
        error.message.includes('ECONNRESET') ||
        error.message.includes('socket hang up') ||
        error.message.includes('Proxy connection');

      if (isRetryableError && retries < maxRetries) {
        retries++;
        continue;
      }

      // 其他错误直接抛出
      break;
    }
  }

  // 所有重试都失败了，抛出最后一个错误
  if (lastError) {
    logger.error(`请求失败，已重试 ${retries} 次: ${lastError.message}`);
    if (lastError.response) {
      logger.error(`响应状态: ${lastError.response.status}`);
      logger.error(`响应数据: ${JSON.stringify(lastError.response.data)}`);
    }
    throw lastError;
  } else {
    // 这种情况理论上不应该发生，但为了安全起见
    const error = new Error(`请求失败，已重试 ${retries} 次，但没有具体错误信息`);
    logger.error(error.message);
    throw error;
  }
 }

/**
 * 检测上传图片内容合规性（仅国内站）
 * 调用 algo_proxy 接口进行图片安全检测，不通过则抛出异常
 *
 * @param imageUri 已上传图片的 URI
 * @param refreshToken 刷新令牌
 * @param regionInfo 区域信息
 */
export async function checkImageContent(
  imageUri: string,
  refreshToken: string,
  regionInfo: RegionInfo
): Promise<void> {
  // 仅国内站需要内容检测
  if (regionInfo.isInternational) return;

  const babiParam = JSON.stringify({
    scenario: "image_video_generation",
    feature_key: "aigc_to_image",
    feature_entrance: "to-generate",
    feature_entrance_detail: "to-generate-algo_proxy",
  });

  logger.info(`开始图片内容安全检测: ${imageUri}`);

  try {
    await request("post", "/mweb/v1/algo_proxy", refreshToken, regionInfo, {
      params: {
        babi_param: babiParam,
      },
      data: {
        scene: "image_face_ip",
        options: { ip_check: true },
        req_key: "benchmark_test_user_upload_image_input",
        file_list: [{ file_uri: imageUri }],
        req_params: {},
      },
    });
    logger.info(`图片内容安全检测通过: ${imageUri}`);
  } catch (error: any) {
    // 区分内容违规(ret=2003等) vs 网络/服务异常
    const isContentViolation = error.message && (
      error.message.includes('2003') ||
      error.message.includes('risk not pass') ||
      error.message.includes('detected risk')
    );
    if (isContentViolation) {
      logger.error(`图片内容安全检测未通过: ${imageUri}, ${error.message}`);
      throw new APIException(
        EX.API_REQUEST_FAILED,
        `图片内容检测未通过，该图片可能包含违规内容`
      );
    }
    // 网络/服务异常不阻塞，仅记录警告
    logger.warn(`图片内容安全检测服务异常(不阻塞): ${imageUri}, ${error.message}`);
  }
}

 /**
  * 预检查文件URL有效性
  *
  * @param fileUrl 文件URL
  */
 export async function checkFileUrl(fileUrl: string) {
  if (util.isBASE64Data(fileUrl)) return;
  await assertSafeExternalHttpUrl(fileUrl);
  const result = await axios.head(fileUrl, {
    timeout: 15000,
    validateStatus: () => true,
  });
  if (result.status >= 400)
    throw new APIException(
      EX.API_FILE_URL_INVALID,
      `File ${fileUrl} is not valid: [${result.status}] ${result.statusText}`
    );
  // 检查文件大小
  if (result.headers && result.headers["content-length"]) {
    const fileSize = parseInt(result.headers["content-length"], 10);
    if (fileSize > FILE_MAX_SIZE)
      throw new APIException(
        EX.API_FILE_EXECEEDS_SIZE,
        `File ${fileUrl} is not valid`
      );
  }
}

/**
 * 上传文件
 *
 * @param refreshToken 用于刷新access_token的refresh_token
 * @param fileUrl 文件URL或BASE64数据
 * @param isVideoImage 是否是用于视频图像
 * @returns 上传结果，包含image_uri
 */
export async function uploadFile(
  refreshToken: string,
  fileUrl: string,
  isVideoImage: boolean = false,
  regionInfo: RegionInfo = buildRegionInfo("cn")
) {
  try {
    logger.info(`开始上传文件: ${fileUrl}, 视频图像模式: ${isVideoImage}`);

    // 预检查远程文件URL可用性
    await checkFileUrl(fileUrl);

    let filename, fileData, mimeType;
    // 如果是BASE64数据则直接转换为Buffer
    if (util.isBASE64Data(fileUrl)) {
      mimeType = util.extractBASE64DataFormat(fileUrl);
      const ext = mime.getExtension(mimeType);
      filename = `${util.uuid()}.${ext}`;
      fileData = Buffer.from(util.removeBASE64DataHeader(fileUrl), "base64");
      logger.info(`处理BASE64数据，文件名: ${filename}, 类型: ${mimeType}, 大小: ${fileData.length}字节`);
    }
    // 下载文件到内存，如果您的服务器内存很小，建议考虑改造为流直传到下一个接口上，避免停留占用内存
    else {
      filename = path.basename(fileUrl);
      logger.info(`开始下载远程文件: ${fileUrl}`);
      ({ data: fileData } = await axios.get(fileUrl, {
        responseType: "arraybuffer",
        // 100M限制
        maxContentLength: FILE_MAX_SIZE,
        // 60秒超时
        timeout: 60000,
      }));
      logger.info(`文件下载完成，文件名: ${filename}, 大小: ${fileData.length}字节`);
    }

    // 获取文件的MIME类型
    mimeType = mimeType || mime.getType(filename);
    logger.info(`文件MIME类型: ${mimeType}`);

    // 构建FormData
    const formData = new FormData();
    const blob = new Blob([fileData], { type: mimeType });
    formData.append('file', blob, filename);

    // 获取上传凭证
    logger.info(`请求上传凭证，场景: ${isVideoImage ? 'video_cover' : 'aigc_image'}`);
    const uploadProofUrl = 'https://imagex.bytedanceapi.com/';
    const proofResult = await request(
      'POST',
      '/mweb/v1/get_upload_image_proof',
      refreshToken,
      regionInfo,
      {
        data: {
          scene: isVideoImage ? 'video_cover' : 'aigc_image',
          file_name: filename,
          file_size: fileData.length,
        }
      }
    );

    if (!proofResult || !proofResult.proof_info) {
      logger.error(`获取上传凭证失败: ${JSON.stringify(proofResult)}`);
      throw new APIException(EX.API_REQUEST_FAILED, '获取上传凭证失败');
    }

    logger.info(`获取上传凭证成功`);

    // 上传文件
    const { proof_info } = proofResult;
    logger.info(`开始上传文件到: ${uploadProofUrl}`);

    const uploadResult = await axios.post(
      uploadProofUrl,
      formData,
      {
        headers: {
          ...proof_info.headers,
          'Content-Type': 'multipart/form-data',
        },
        params: proof_info.query_params,
        timeout: 60000,
        validateStatus: () => true, // 允许任何状态码以便详细处理
      }
    );

    logger.info(`上传响应状态: ${uploadResult.status}`);

    if (!uploadResult || uploadResult.status !== 200) {
      logger.error(`上传文件失败: 状态码 ${uploadResult?.status}, 响应: ${JSON.stringify(uploadResult?.data)}`);
      throw new APIException(EX.API_REQUEST_FAILED, `上传文件失败: 状态码 ${uploadResult?.status}`);
    }

    // 验证 proof_info.image_uri 是否存在
    if (!proof_info.image_uri) {
      logger.error(`上传凭证中缺少 image_uri: ${JSON.stringify(proof_info)}`);
      throw new APIException(EX.API_REQUEST_FAILED, '上传凭证中缺少 image_uri');
    }

    logger.info(`文件上传成功: ${proof_info.image_uri}`);

    // 返回上传结果
    return {
      image_uri: proof_info.image_uri,
      uri: proof_info.image_uri,
    }
  } catch (error) {
    logger.error(`文件上传过程中发生错误: ${error.message}`);
    throw error;
  }
}

/**
 * 检查请求结果
 *
 * @param result 结果
 */
export function checkResult(result: AxiosResponse) {
  const { ret, errmsg, data } = result.data;
  if (!Number.isFinite(Number(ret))) return result.data;
  if (ret === '0') return data;

  // 使用统一错误处理器
  JimengErrorHandler.handleApiResponse(result.data as JimengErrorResponse, {
    context: '即梦API请求',
    operation: '请求'
  });
}

/**
 * Token切分
 *
 * @param authorization 认证字符串
 */
export function tokenSplit(authorization: string) {
  if (!/^Bearer\s+/i.test(authorization)) return [];
  return authorization
    .replace(/^Bearer\s+/i, "")
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);
}

/**
 * 获取Token存活状态
 */
export async function getTokenLiveStatus(refreshToken: string, regionInfo: RegionInfo) {
  try {
    if (regionInfo.isInternational) {
      // 国际区 (US/HK/JP/SG) 使用不同的 passport 端点
      return await checkInternationalTokenLive(refreshToken, regionInfo);
    }
    // CN 区: /passport/account/info/v2
    const result = await request(
      "POST",
      "/passport/account/info/v2",
      refreshToken,
      regionInfo,
      {
        params: {
          account_sdk_source: "web",
        },
      }
    );
    const resultObj = (result && typeof result === "object")
      ? (result as Record<string, unknown>)
      : {};
    const nestedData =
      resultObj.data && typeof resultObj.data === "object"
        ? (resultObj.data as Record<string, unknown>)
        : null;
    // request 内部已调用 checkResult，ret!=0 会抛错；判活以 user_id 为准。
    return Boolean(resultObj.user_id || nestedData?.user_id);
  } catch {
    return false;
  }
}

/**
 * 国际区 token 判活 — 使用 dreamina.capcut.com/passport/web/account/info/
 * 国际区没有 /passport/account/info/v2 端点，需要走前端域名
 */
async function checkInternationalTokenLive(refreshToken: string, regionInfo: RegionInfo): Promise<boolean> {
  const aid = getAssistantId(regionInfo);
  const countryCode = regionInfo.isUS ? "us" : regionInfo.isJP ? "jp" : regionInfo.isHK ? "hk" : "sg";
  const cookie = generateCookie(refreshToken);
  try {
    const response = await axios.get(
      `${INTERNATIONAL_FRONTEND_ORIGIN}/passport/web/account/info/`,
      {
        params: {
          aid,
          account_sdk_source: "web",
          sdk_version: "2.1.10-tiktok",
          language: countryCode === "jp" ? "ja" : "en",
        },
        headers: {
          ...FAKE_HEADERS,
          Cookie: cookie,
          Referer: `${INTERNATIONAL_FRONTEND_ORIGIN}/ai-tool/home/`,
          Origin: INTERNATIONAL_FRONTEND_ORIGIN,
          Appid: String(aid),
          "store-country-code": countryCode,
          "store-country-code-src": "uid",
        },
        timeout: 15000,
      }
    );
    const data = response.data;
    if (data && typeof data === "object") {
      const obj = data as Record<string, unknown>;
      if (obj.user_id || obj.email || obj.data) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}
